import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getModules, getCapabilities, createCapability, updateCapability, deleteCapability } from '../api/capabilitiesApi';
import useModalEffect from '../../../hooks/useModalEffect';

const CapabilitiesManagement = () => {
  const [modules, setModules] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  useModalEffect(showModal);
  const [editingCapability, setEditingCapability] = useState(null);
  const [formData, setFormData] = useState({
    capabilityKey: '',
    capabilityName: '',
    description: '',
    moduleId: '',
    isActive: true
  });

  useEffect(() => {
    loadModules();
    loadCapabilities();
  }, []);

  useEffect(() => {
    if (selectedModule) {
      loadCapabilities(selectedModule);
    } else {
      loadCapabilities();
    }
  }, [selectedModule]);

  const loadModules = async () => {
    try {
      const data = await getModules();
      setModules(data || []);
    } catch (error) {
      console.error('Error loading modules:', error);
      setModules([]);
    }
  };

  const loadCapabilities = async (moduleId = null) => {
    setIsLoading(true);
    try {
      const data = await getCapabilities(moduleId);
      setCapabilities(data || []);
    } catch (error) {
      console.error('Error loading capabilities:', error);
      setCapabilities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCapability(null);
    setFormData({
      capabilityKey: '',
      capabilityName: '',
      description: '',
      moduleId: selectedModule ? parseInt(selectedModule) : null,
      isActive: true
    });
    setShowModal(true);
  };

  const handleEdit = (capability) => {
    setEditingCapability(capability);
    setFormData({
      capabilityKey: capability.capabilityKey,
      capabilityName: capability.capabilityName,
      description: capability.description || '',
      moduleId: capability.moduleId,
      isActive: capability.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (capabilityId) => {
    if (window.confirm('Are you sure you want to delete this capability?')) {
      try {
        await deleteCapability(capabilityId);
        alert('Capability deleted successfully!');
        loadCapabilities(selectedModule);
      } catch (error) {
        console.error('Error deleting capability:', error);
        alert('Error deleting capability: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data with proper types
      const submitData = {
        ...formData,
        moduleId: formData.moduleId ? parseInt(formData.moduleId) : null
      };
      
      console.log('=== DEBUG: Submit Data ===');
      console.log('Original formData:', formData);
      console.log('Processed submitData:', submitData);
      console.log('=== END DEBUG ===');
      
      if (editingCapability) {
        await updateCapability(editingCapability.id, submitData);
        alert('Capability updated successfully!');
      } else {
        await createCapability(submitData);
        alert('Capability created successfully!');
      }
      
      setShowModal(false);
      loadCapabilities(selectedModule);
    } catch (error) {
      console.error('Error saving capability:', error);
      alert('Error saving capability: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getModuleName = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module ? module.moduleName : 'Unknown Module';
  };

  return (
    <div>
      <div className="row g-2 align-items-center mb-4">
        <div className="col">
          <h4 className="m-0">Manage Capabilities</h4>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <button
            className="btn btn-primary"
            onClick={handleCreate}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 5l0 14"/>
              <path d="M5 12l14 0"/>
            </svg>
            Add Capability
          </button>
        </div>
      </div>

      {/* Module Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          <label className="form-label fw-bold">Filter by Module</label>
          <select
            className="form-select"
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
          >
            <option value="">All Modules</option>
            {modules.map((module) => (
              <option key={module.id} value={module.id}>
                {module.moduleName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Capabilities Table */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-vcenter">
                <thead>
                  <tr>
                    <th>Capability Key</th>
                    <th>Capability Name</th>
                    <th>Description</th>
                    <th>Module</th>
                    <th>Status</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {capabilities.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No capabilities found
                      </td>
                    </tr>
                  ) : (
                    capabilities.map((capability) => (
                      <tr key={capability.id}>
                        <td>
                          <code className="text-primary">{capability.capabilityKey}</code>
                        </td>
                        <td className="fw-bold">{capability.capabilityName}</td>
                        <td>{capability.description || '-'}</td>
                        <td>
                          <span className="badge bg-blue-lt">
                            {getModuleName(capability.moduleId)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${capability.isActive ? 'bg-green-lt' : 'bg-red-lt'}`}>
                            {capability.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="btn-list">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(capability)}
                              title="Edit"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"/>
                                <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"/>
                                <path d="M16 5l3 3"/>
                              </svg>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(capability.id)}
                              title="Delete"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M4 7l16 0"/>
                                <path d="M10 11l0 6"/>
                                <path d="M14 11l0 6"/>
                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/>
                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCapability ? 'Edit Capability' : 'Add New Capability'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Capability Key <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="capabilityKey"
                          value={formData.capabilityKey}
                          onChange={handleInputChange}
                          placeholder="e.g., view_own, create, edit"
                          required
                        />
                        <div className="form-text">Unique identifier for the capability (lowercase, underscore separated)</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Capability Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="capabilityName"
                          value={formData.capabilityName}
                          onChange={handleInputChange}
                          placeholder="e.g., View (Own), Create, Edit"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Describe what this capability allows users to do"
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Module <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          name="moduleId"
                          value={formData.moduleId}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select a module</option>
                          {modules.map((module) => (
                            <option key={module.id} value={module.id}>
                              {module.moduleName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label">
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingCapability ? 'Update' : 'Create'} Capability
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
    </div>
  );
};

export default CapabilitiesManagement;
